import { useState, useEffect } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const links = [
  { name: "Servizi", href: "#servizi", icon: "grid" },
  { name: "Piattaforme", href: "#piattaforme", icon: "box" },
  { name: "Innovation Lab", href: "/innovation-lab", isPage: true, icon: "cpu" },
  { name: "Prezzi", href: "#prezzi", icon: "tag" },
  { name: "Chi Siamo", href: "#chi-siamo", icon: "users" },
  { name: "Blog", href: "#blog", icon: "file" },
  { name: "Contatti", href: "#contatti", icon: "mail" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const NavLink = ({ link }) => {
    const cls = `text-xs font-mono tracking-[0.2em] uppercase transition-colors duration-300 ${
      link.isPage ? "text-[#FF3366] hover:text-white" : "text-white/60 hover:text-[#FF3366]"
    }`;
    return link.isPage ? (
      <Link to={link.href} data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`} className={cls}>
        {link.name}
      </Link>
    ) : (
      <a href={link.href} data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`} className={cls}>
        {link.name}
      </a>
    );
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 transition-all duration-500 ${
        isOpen ? "z-[9999] bg-[#050505]" :
        scrolled
          ? "z-50 bg-[#050505]/95 backdrop-blur-xl border-b border-white/10"
          : "z-50 bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between h-16 sm:h-20">
        <a href="/" data-testid="navbar-logo" className="font-heading text-xl sm:text-2xl font-black tracking-tighter text-white hover:text-[#FF3366] transition-colors">
          NINFA
        </a>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((link) => <NavLink key={link.name} link={link} />)}
        </div>

        <a
          href="#contatti"
          data-testid="nav-cta-button"
          className="hidden lg:block bg-[#FF3366] text-white font-bold uppercase tracking-widest px-6 py-2.5 text-xs hover:bg-white hover:text-black transition-colors duration-300"
        >
          CONTATTACI
        </a>

        {/* Mobile menu button */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden text-white p-2 -mr-2"
          aria-label="Menu"
        >
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile fullscreen menu */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-[#050505] z-[9998] overflow-y-auto" style={{ position: 'fixed' }}>
          <div className="px-4 py-6 space-y-1">
            {links.map((link) => (
              link.isPage ? (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-between py-4 border-b border-white/5"
                >
                  <span className="font-heading text-lg font-bold text-[#FF3366]">{link.name}</span>
                  <ChevronRight size={18} className="text-[#FF3366]" />
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-between py-4 border-b border-white/5"
                >
                  <span className="font-heading text-lg font-bold text-white/80">{link.name}</span>
                  <ChevronRight size={18} className="text-white/30" />
                </a>
              )
            ))}
          </div>
          <div className="px-4 pt-4 pb-8">
            <a
              href="#contatti"
              onClick={() => setIsOpen(false)}
              className="block w-full text-center bg-[#FF3366] text-white font-bold uppercase tracking-widest py-4 text-sm"
            >
              CONTATTACI
            </a>
            <a
              href="tel:info@ninfa.eu"
              className="block w-full text-center mt-3 border border-white/10 text-white/60 font-mono py-3 text-xs tracking-wider"
            >
              info@ninfa.eu
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
